import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { fetchAIPPTComponentList, fetchAIPPTMaterialDetail, fetchAIPPTSmartComponentList } from '@/services/ai';
import './ai-ppt-template-demo.less';
import { cloneDeep, slice } from 'lodash';
import { Button } from '@meitu/saas-ui';
import { Editor } from '@meitu/saas-editor-core';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { ILayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { packageTemplateConf } from '@/hooks/fetch-template';
import { isPPTDemoAndShowTemplateComponentList } from '@/hooks/fetch-template/editor-mode';
import { transferLayersToClientDoText, transferTemplateToClient } from '@/hooks/fetch-template/server-to-client';
import {
    adjustImageLayerImage,
    findTemplateDirectoryLayout,
    IPPTContentProps,
    Layer,
    PPTTemplate,
} from '../apply-ppt-template/apply-ppt-template';
import { getJSONByUrl } from '../apply-ppt-template/utils';
import gptContent, { smartContent } from './gpt-content';
import RenderStage from './render-stage';
import { adjustScale, getLayersArray, getSmartComponentData } from './utils';

function getRandomElement(arr: any[]) {
    return arr[Math.floor(Math.random() * arr.length)];
}

const PAGE_SIZE = 30;
function getPageData(array: any[], page: number, pageSize = PAGE_SIZE) {
    const start = page * pageSize;
    const end = start + pageSize;
    return slice(array, start, end);
}

// 通过组件去获取 内容数据
const getPPTContentDataByComponents = async (
    pptContentData: IPPTContentProps[],
    transferTemplateToClient: (serverTemplate: any) => Promise<any>,
    directoryLayout: string,
    specifyComponentDatas?: any[],
) => {
    const contentData = [] as any;
    if (!specifyComponentDatas) {
        return pptContentData;
    }

    for (let i = 0; i < specifyComponentDatas.length; i++) {
        const specifyComponentData = specifyComponentDatas[i];
        const datas = pptContentData.filter((content) => {
            if (content.page_type === '内容') {
                const contentNums = Object.keys(content.data).filter((item) => item.includes('列表标题')).length;
                return (
                    specifyComponentData.component_point === contentNums && specifyComponentData.component_type === 2
                );
            } else if (content.page_type === '目录' && directoryLayout === specifyComponentData.layout) {
                const contentNums = Object.keys(content.data).filter((item) => item.includes('目录标题')).length;
                return (
                    specifyComponentData.component_point === contentNums && specifyComponentData.component_type === 1
                );
            }
        });

        const data = getRandomElement(datas);

        if (data) {
            const templateData = await getJSONByUrl(specifyComponentData.json_url);
            const templateConf = await transferTemplateToClient(cloneDeep(templateData));
            const result = cloneDeep(data);
            result.componentData = { templateConf };
            result.component_id = specifyComponentData.id;
            contentData.push(result);
        }
    }

    return contentData;
};

const getPPTContentDataBySmartComponents = async (
    gptContent: any[],
    transferTemplateToClient: (serverTemplate: any) => Promise<any>,
    directoryLayout: string,
    smartComponentList: any[],
    fonts: any[],
    templateList: any[],
    componentList: any[],
) => {
    if (!smartComponentList) {
        return gptContent;
    }

    const contentData = [] as any;
    for (let i = 0; i < smartComponentList.length; i++) {
        const specifyComponentData = smartComponentList[i];
        const smartComponentData = await getSmartComponentData(
            templateList,
            componentList,
            specifyComponentData.id,
            fonts,
        );

        contentData.push(...smartComponentData);
    }

    return contentData;
};

const AIPPTTemplateDemo: React.FC<{ editor: Editor }> = ({ editor }) => {
    const fonts = editor.font.getList();
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [scale, setScale] = useState<number>(1);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [templateLayers, setTemplateLayers] = useState<any[]>([]);
    const [currentTemplates, setCurrentTemplates] = useState<any[]>([]);
    useEffect(() => {
        const templates = getPageData(templateLayers, currentPage);

        setCurrentTemplates(templates);
        const { stages, extraInfos } = packageTemplateConf(templates, {}, true);

        editor.stage.insertWithoutHistory(stages, extraInfos, undefined, true);
    }, [currentPage]);

    useEffect(() => {
        if (fonts.length === 0) {
            return;
        }
        if (!isPPTDemoAndShowTemplateComponentList()) {
            return;
        }

        const getInitData = async () => {
            const layerAndStageIndexMap: { [layerId: string]: number } = {};
            const queryParams: any = queryString.parse(window.location.search);
            const { templateId } = queryParams;

            const rsp = await fetchAIPPTMaterialDetail(templateId);
            let templateData = JSON.parse(rsp.data.data.formula);

            const componentListRsp = await fetchAIPPTComponentList();
            const smartComponentListRsp = await fetchAIPPTSmartComponentList();
            const componentList = componentListRsp.data.data.data;
            const smartComponentList = smartComponentListRsp.data.data.data;
            const transferToClient = async (templateData: any) => {
                const data = await transferTemplateToClient(templateData, fonts, '', false);
                return data.template.templateConf;
            };
            const clientData = await transferTemplateToClient(templateData, fonts, '', false, true);

            templateData = clientData.template;
            const directoryLayout = findTemplateDirectoryLayout(templateData.templateConf);

            const smartContents = await getPPTContentDataBySmartComponents(
                smartContent,
                transferToClient,
                directoryLayout,
                smartComponentList,
                fonts,
                [{ id: templateId }],
                componentList,
            );

            const contents = await getPPTContentDataByComponents(
                gptContent as any,
                transferToClient,
                directoryLayout,
                componentList,
            );
            const templateConf = PPTTemplate.generate({
                pptContents: contents,
                templateData,
            });

            templateConf.unshift(
                ...smartContents.map((item: any) => {
                    return {
                        width: 1920,
                        height: 1080,
                        layers: item,
                    };
                }),
            );

            const allLayers = [] as ILayerProps[];
            const allImageLayers = [] as ILayerProps[];
            templateConf.forEach((item) => {
                traverseTrees(item.layers, (layer: ILayerProps) => {
                    if (layer.layerType === LayerType.TEXT_LAYER) {
                        allLayers.push(layer);
                    }
                    if (layer.layerType === LayerType.IMAGE_LAYER) {
                        allImageLayers.push(layer);
                    }
                });
            });

            for (let i = 0; i < allImageLayers.length; i++) {
                const layer = allImageLayers[i];
                if (layer.layerType === LayerType.IMAGE_LAYER) {
                    await adjustImageLayerImage(layer);
                }
            }

            console.log('templateConf===', JSON.stringify(templateConf));
            templateConf.forEach((item, index) => {
                traverseTrees(item.layers, (layer: ILayerProps) => {
                    layerAndStageIndexMap[layer.id] = index;
                    if (layer.layerType === LayerType.TEXT_LAYER) {
                        const { extraInfo } = layer;

                        if (extraInfo?.label) {
                            const { textExpend } = extraInfo.label;
                            if (textExpend) {
                                Layer.adjustTextSize(layer, textExpend);
                            }
                        }
                    }
                });
            });

            await transferLayersToClientDoText(allLayers, fonts, layerAndStageIndexMap, false);

            templateConf.map((item: any, index) => {
                item.index = index + 1;
            });
            setTemplateLayers(templateConf);
            const templates = getPageData(cloneDeep(templateConf), currentPage);
            setCurrentTemplates(templates);

            const { stages, extraInfos } = packageTemplateConf(templates, {}, true);
            await editor.stage.insertWithoutHistory(stages, extraInfos, undefined, true);
        };

        getInitData();
    }, [fonts]);

    useEffect(() => {
        if (!isPPTDemoAndShowTemplateComponentList()) {
            return;
        }
        function scaleStage() {
            adjustScale(setScale);
        }

        // 初始化时调整缩放
        setTimeout(() => {
            scaleStage();
        }, 1000);

        // 监听窗口大小变化
        window.addEventListener('resize', scaleStage);

        return () => {
            window.removeEventListener('resize', scaleStage);
        };
    }, [templateLayers]);

    if (!isPPTDemoAndShowTemplateComponentList()) {
        return null;
    }

    if (templateLayers.length === 0 || fonts.length === 0) {
        return (
            <div className="ai-ppt-template-demo-loading">
                <div className="loading-text">加载中请稍后...</div>
            </div>
        );
    }

    return (
        <div className="ai-ppt-template-demo-container">
            <div className="operate">
                <Button type="primary" onClick={() => setCurrentPage(currentPage - 1 < 0 ? 0 : currentPage - 1)}>
                    上一页
                </Button>
                <Button
                    type="primary"
                    onClick={() =>
                        setCurrentPage(
                            currentPage + 1 > templateLayers.length / PAGE_SIZE ? currentPage : currentPage + 1,
                        )
                    }
                >
                    下一页
                </Button>
            </div>
            <div className="ai-ppt-template-demo">
                {currentTemplates.map((item, index) => {
                    return (
                        <div className="demo-item" key={index}>
                            <RenderStage stageIndex={item.index} scale={scale} editor={editor} componentId={''} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AIPPTTemplateDemo;
